import { Formik } from 'formik';
import moment from 'moment';
import React from 'react';
import { Container, Row } from 'react-grid-system';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import Button from '../../../components/Button';
import FlashMessage from '../../../components/FlashMessage';
import DatePicker from '../../../components/FormFields/DatePicker';
import FieldWithLabel from '../../../components/FormFields/FieldWithLabel';
import Modal from '../../../components/Modal';
import { Header3, P2 } from '../../../components/Typography';
import { DATE_FORMAT } from '../../../constants/dates';
import {
  UPDATE_BANK_TRANSACTIONS_ENABLED_AT_MODAL_STORE_KEY,
  UPDATE_BANK_TRANSACTIONS_ENABLED_AT_STORE_KEY,
  updateBankTransactionsEnabledAtApiRequested,
} from '../../../redux/claim/claim.actions';
import { createLoadingSelector } from '../../../redux/loading/loading.selector';
import { FLASH_MESSAGE_TIMEOUT, useFlashMessage } from '../../FlashMessage';

interface EditBankTransactionsEnabledAtModalProps {
  claimId: string;
  onDismiss: () => void;
}

interface EditBankTransactionsEnabledAtFormValues {
  bankTransactionsEnabledAt?: Date;
}

const Actions = styled(Row)`
  display: flex;
  justify-content: flex-end;
  column-gap: 8px;
  padding-top: 24px;
`;

const EditBankTransactionsEnabledAtModal: React.FC<EditBankTransactionsEnabledAtModalProps> = ({ claimId, onDismiss }) => {
  const dispatch = useDispatch();
  const isSubmitting = useSelector(createLoadingSelector([UPDATE_BANK_TRANSACTIONS_ENABLED_AT_STORE_KEY]));
  const [flashMessage, onDismissFlashMessage] = useFlashMessage([UPDATE_BANK_TRANSACTIONS_ENABLED_AT_MODAL_STORE_KEY]);
  const handleDismiss = () => {
    onDismissFlashMessage();
    onDismiss();
  };

  return (
    <Modal onDismiss={handleDismiss}>
      {flashMessage ? (
        <FlashMessage payload={flashMessage} onDismiss={onDismissFlashMessage} timeRemaining={FLASH_MESSAGE_TIMEOUT} />
      ) : null}
      <Header3 style={{ marginBottom: 6 }}>Set Earliest Bank Transfer Import Date</Header3>
      <P2>
        Imported bank transfers dated before this are not applied to the claim. The date can only be set once, and every claim and
        arbitration order on the claim must have automatic refund and dispute handling.
      </P2>
      <Formik<EditBankTransactionsEnabledAtFormValues>
        initialValues={{ bankTransactionsEnabledAt: undefined }}
        onSubmit={(values) => {
          if (!values.bankTransactionsEnabledAt) return;
          onDismissFlashMessage();
          dispatch(
            updateBankTransactionsEnabledAtApiRequested({
              claimId,
              bankTransactionsEnabledAt: moment(values.bankTransactionsEnabledAt).format('YYYY-MM-DD'),
            }),
          );
        }}
      >
        {({ handleBlur, handleSubmit, setFieldValue, values }) => (
          <form onSubmit={handleSubmit}>
            <FieldWithLabel label="Earliest bank transfer import date">
              <DatePicker
                name="bankTransactionsEnabledAt"
                value={values.bankTransactionsEnabledAt}
                onBlur={handleBlur}
                dateFormat={DATE_FORMAT}
                placeholder={'DD/MM/YYYY'}
                onChange={(date) => setFieldValue('bankTransactionsEnabledAt', date)}
              />
            </FieldWithLabel>
            <Container fluid>
              <Actions>
                <Button buttonType="secondary" noArrow={true} onClick={handleDismiss} type="button">
                  Cancel
                </Button>
                <Button disabled={isSubmitting || !values.bankTransactionsEnabledAt} type="submit">
                  Save
                </Button>
              </Actions>
            </Container>
          </form>
        )}
      </Formik>
    </Modal>
  );
};

export default EditBankTransactionsEnabledAtModal;
